/**
 * FileInfoDisplay - Shared file information display component
 * 
 * Provides consistent file information display across all tools
 * Shows file name, dimensions, size, and format
 * 
 * Used by both ImageConverterTool and QuickConverterTool to ensure UI consistency
 */

export interface FileInfo {
  name: string;
  format: string;
  width: number;
  height: number;
  size: number;
}

export class FileInfoDisplay {
  private fileInfoElement: HTMLElement | null = null;
  private fileNameElement: HTMLElement | null = null;
  private fileMetaElement: HTMLElement | null = null;

  /**
   * Initialize the file info display with DOM elements
   */
  init(): void {
    this.fileInfoElement = document.getElementById('selectedFileInfo');
    this.fileNameElement = document.getElementById('fileName');
    this.fileMetaElement = document.getElementById('fileMeta');

    if (!this.fileInfoElement) {
      console.error('FileInfoDisplay: selectedFileInfo element not found');
    }
  }

  /**
   * Show file information
   */
  show(info: FileInfo): void {
    if (!this.fileInfoElement) {
      console.error('FileInfoDisplay: Not initialized');
      return;
    }

    // Update file name
    if (this.fileNameElement) {
      this.fileNameElement.textContent = info.name;
    }

    // Update file metadata (dimensions, size, format)
    if (this.fileMetaElement) {
      const sizeStr = this.formatFileSize(info.size);
      this.fileMetaElement.textContent = `${info.width} × ${info.height} • ${sizeStr} • ${info.format}`;
    }

    // Make visible
    this.fileInfoElement.classList.add('active');
  }

  /**
   * Hide file information
   */
  hide(): void {
    if (this.fileInfoElement) {
      this.fileInfoElement.classList.remove('active');
    }

    // Clear content
    if (this.fileNameElement) {
      this.fileNameElement.textContent = '';
    }
    if (this.fileMetaElement) {
      this.fileMetaElement.textContent = '';
    }
  }

  /**
   * Format file size for display (KB, MB, etc.)
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  /**
   * Check if file info is currently visible
   */
  isVisible(): boolean {
    return this.fileInfoElement?.classList.contains('active') || false;
  }
}
